import { Component, OnDestroy, OnInit, input } from '@angular/core';
import { FormGroup, UntypedFormBuilder, Validators } from '@angular/forms';
import { take } from 'rxjs';
import { filter } from 'rxjs/operators';
import { SubSink } from 'subsink';
import { assert } from '../../../../../../core/assert/assert';
import { defaultPalette } from '../../../../../../core/constants-repository/color-palette.constants';
import { UserOperation } from '../../../../../../core/enums/user-operation';
import { ClientModel } from '../../../../../../core/models/client/client.model';
import { ProjectModel } from '../../../../../../core/models/project/project.model';
import { StatusModel } from '../../../../../../core/models/project/status.model';
import { ShiveDialogService } from '../../../../../../core/services/controls/shive-dialog.service';
import { ClientsHttpService } from '../../../../../../core/state/clients/clients-http.service';
import { DropdownOption } from '../../../../../../shared/data-types/dropdown-option';
import {
  FormControlNameRecord,
  FormControlValue,
} from '../../../../../../shared/data-types/form-types';
import {
  dropdownOptionToEntity,
  entityToDropdownOption,
  isDropdownValue,
  observableToDropdownOptions,
} from '../../../../../../shared/functions/dropdown-functions';
import { ClientStatusFilter } from '../../../../../client/client-types';
import { CreateEditClientDataService } from '../../../../../client/create-edit-client/create-edit-client-data.service';
import { ProjectDataService } from '../../../state-and-data-handling/project-data.service';
import { CreateClientDialogComponent } from '../../dialogs/create-client-dialog/create-client-dialog.component';
import { ManageProjectService } from '../manage-project.service';

@Component({
  selector: 'app-project-base-form',
  templateUrl: './project-base-form.component.html',
  styleUrls: ['./project-base-form.component.scss'],
  standalone: false,
})
export class ProjectBaseFormComponent implements OnInit, OnDestroy {
  readonly projectStatuses = input<StatusModel[]>();
  public statusOptions: readonly DropdownOption[] = [];
  public clients: DropdownOption[] = [];
  public projectBaseForm: FormGroup;
  public formControlNames: FormControlNameRecord = {
    ProjectName: 'name',
    StartsAt: 'startsAt',
    EndsAt: 'endsAt',
    Client: 'client',
    ProjectId: 'projectId',
    ProjectStatus: 'status',
    ProjectColor: 'color',
    Currency: 'currency',
    Remark: 'remark',
  };
  public selectedClient: DropdownOption;
  public readonly defaultPalette = defaultPalette;
  public readonly UserOperation = UserOperation;
  private readonly subs = new SubSink();

  constructor(
    public clientsHttpService: ClientsHttpService,
    private readonly projectDataService: ProjectDataService,
    private readonly createEditClientDataService: CreateEditClientDataService,
    private readonly shiveDialogService: ShiveDialogService,
    private readonly formBuilder: UntypedFormBuilder,
    private readonly manageProjectService: ManageProjectService,
  ) {}

  ngOnInit(): void {
    this.statusOptions = this.projectStatuses().map((status) => entityToDropdownOption(status));
    this.bootstrap();
  }

  ngOnDestroy(): void {
    this.projectBaseForm.reset();
    this.subs.unsubscribe();
  }

  public formValueChanged([value, formCtrlName]: FormControlValue): void {
    assert(typeof formCtrlName !== 'undefined', `Form control name is undefined: ${formCtrlName}`);

    const projectClone = this.projectDataService.getProjectClone();
    if (isDropdownValue(value)) {
      projectClone[formCtrlName] = dropdownOptionToEntity(value as DropdownOption);
    } else {
      projectClone[formCtrlName] = value;
    }

    this.projectDataService.updateProject(projectClone);
    this.projectDataService.userOperationConducted$.next(true);
  }

  public openClientDialog(): void {
    const ref = this.shiveDialogService.open({
      content: CreateClientDialogComponent,
      width: 572,
      title: 'Neuer Kunde',
    });

    this.subs.sink = ref.result
      .pipe(
        filter((value) => value !== UserOperation.Cancel),
        take(1),
      )
      .subscribe((clientModel: ClientModel) => {
        this.createEditClientDataService.sendRequests(clientModel);
        this.selectedClient = entityToDropdownOption(clientModel);
      });
  }

  private buildForm(project: ProjectModel): void {
    const projectData = {
      name: project.name,
      remark: project.remark,
      color: project.color,
      startsAt: project.startsAt ?? new Date(),
      endsAt: project.endsAt ?? new Date(),
      projectStatus: project.status,
    };

    this.projectBaseForm = this.formBuilder.group({
      [this.formControlNames.ProjectName]: [projectData.name, Validators.required],
      [this.formControlNames.ProjectId]: [null],
      [this.formControlNames.Status]: [projectData.projectStatus, Validators.required],
      [this.formControlNames.ProjectColor]: [projectData.color, Validators.required],
      [this.formControlNames.StartsAt]: [projectData.startsAt, Validators.required],
      [this.formControlNames.EndsAt]: [projectData.endsAt, Validators.required],
      [this.formControlNames.Currency]: [null],
      [this.formControlNames.Remark]: [projectData.remark, null],
    });

    this.manageProjectService.projectBaseForm = this.projectBaseForm;
  }

  private bootstrap(): void {
    const projectClone = this.projectDataService.getProjectClone();
    this.buildForm(projectClone);
    this.selectedClient = entityToDropdownOption(projectClone.client);

    this.projectBaseForm.controls[this.formControlNames.Status].setValue(
      entityToDropdownOption(projectClone.status),
    );

    observableToDropdownOptions(this.clientsHttpService.getClients(ClientStatusFilter.Active))
      .pipe(take(1))
      .subscribe((clientOptions) => (this.clients = clientOptions));
  }
}
